import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { data, portal } from "@aawaz/api-client";
import {
  Badge, Button, Card, ChannelLogo, EmptyState, ErrorBanner, PageHeader, SkeletonRows, Stat, Table, fmtDate, humanize, sourceLabel,
} from "@aawaz/ui";
import { useSession } from "../session";
import { inr } from "./Home";

interface Source {
  binding_id: string; client_org_id: string | null; provider: string; company_name: string | null; connection_status: string;
  write_enabled: boolean; last_synced_at: string | null; last_error: string | null; counts: Record<string, number>;
}
interface Post { client: string | null; status: string; blockers: string[]; at: string; command_id: string | null }
interface Voucher {
  client: string | null; provider: string; voucher_type: string | null; number: string | null; name: string | null;
  doc_date: string | null; amount: string | null; origin: string | null; synced_at: string;
}
interface Activity { autopost: Post[]; posted_30d: number; held_30d: number; recent: Voucher[] }

/** Books: is each linked organization / Tally company in sync, what AawazAI posted for the firm, and why anything was held. */
export function BooksPage() {
  const s = useSession();
  const qc = useQueryClient();
  const sources = useQuery({ queryKey: ["ca-books-sources"], refetchInterval: 30_000,
    queryFn: () => data<{ data: Source[] }>(portal.GET("/portal/v1/ca/books/sources")) });
  const act = useQuery({ queryKey: ["ca-books-activity"], queryFn: () => data<Activity>(portal.GET("/portal/v1/ca/books/activity")) });
  const sync = useMutation({
    mutationFn: (id: string) => data(portal.POST("/portal/v1/ca/books/sources/{binding_id}:sync", { params: { path: { binding_id: id } } })),
    onSuccess: () => setTimeout(() => void qc.invalidateQueries({ queryKey: ["ca-books-sources"] }), 4000),
  });
  const rows = sources.data?.data ?? [];
  const healthy = rows.filter((r) => r.last_synced_at && !r.last_error).length;
  const a = act.data;
  const ca = s.has("ca");
  return (
    <>
      <PageHeader title="Books" eyebrow={ca ? "Zoho Books and Tally, per client" : "Zoho Books and Tally"}
        description="Parties, ledgers and vouchers sync every 15 minutes. Bills you approve post back as drafts; nothing is ever deleted."
        actions={s.can("manage_connections") ? <Link className="ui-btn ui-btn-primary" to="/channels"><span>Connect books</span></Link> : null} />
      <ErrorBanner error={sources.error ?? act.error ?? sync.error} />
      <div className="ui-stats">
        <Stat label="Books connected" value={rows.length} icon="book" tone="brand" note={rows.length ? `${healthy} in sync` : "None yet"} />
        <Stat label="Posted · 30 days" value={a?.posted_30d ?? 0} icon="check" tone="green" note="Drafts created in the books" />
        <Stat label="Held for a person · 30 days" value={a?.held_30d ?? 0} icon="alert" tone={a?.held_30d ? "amber" : undefined}
              note={a?.held_30d ? "See why below" : "Nothing held"} />
        <Stat label="Entries synced recently" value={a?.recent.length ?? 0} icon="refresh" note="Latest 20 shown below" />
      </div>

      <Card title="Sync health" subtitle={ca ? "One row per client organization or Tally company" : undefined} flush>
        {sources.isLoading ? <div style={{ padding: 22 }}><SkeletonRows rows={3} /></div> : rows.length === 0 ? (
          <EmptyState icon="book" title="No books connected"
            action={s.can("manage_connections") ? <Link className="ui-btn ui-btn-primary" to="/channels">Connect Zoho Books or Tally</Link> : undefined}>
            Once connected, AawazAI matches bills to parties by GSTIN, flags what is missing from the books, and can post approved bills back.
          </EmptyState>
        ) : (
          <div className="ui-conn-list">
            {rows.map((r) => {
              const vouchers = Object.entries(r.counts).filter(([k]) => k.startsWith("voucher:")).reduce((n, [, v]) => n + v, 0);
              const reauth = r.last_error?.startsWith("REAUTH_REQUIRED");
              return (
                <div key={r.binding_id} className="ui-conn">
                  <span className="ui-tile-logo"><ChannelLogo kind={r.provider} size={24} /></span>
                  <div className="ui-conn-main">
                    <div className="ui-row" style={{ gap: 8 }}>
                      <strong>{r.company_name ?? sourceLabel(r.provider)}</strong>
                      <span className={`ui-dot ui-${r.last_error ? "red" : r.last_synced_at ? "green" : "amber"}`} />
                    </div>
                    <div className="ui-conn-meta">
                      {r.last_error ? <span className="ui-text-red">{reauth ? "Sign-in expired. Reconnect from Channels." : r.last_error.slice(0, 140)}</span>
                        : r.last_synced_at ? <>Synced {fmtDate(r.last_synced_at)} · {vouchers} changed entr{vouchers === 1 ? "y" : "ies"} last run</>
                        : "Waiting for the first sync"}
                    </div>
                  </div>
                  <Badge value={r.write_enabled ? "posting on" : "read only"} tone={r.write_enabled ? "green" : "grey"} />
                  {s.can("manage_connections") && r.provider !== "tally" ? (
                    <Button size="sm" icon="refresh" onClick={() => sync.mutate(r.binding_id)} busy={sync.isPending && sync.variables === r.binding_id}>Sync now</Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="ui-split">
        <Card title="Posted by AawazAI" subtitle="Last 30 days" actions={s.can("tenant_settings") ? <Link to="/settings/automation">Auto-posting rules</Link> : undefined} flush>
          {act.isLoading ? <div style={{ padding: 22 }}><SkeletonRows rows={4} /></div> : (a?.autopost.length ?? 0) === 0 ? (
            <EmptyState icon="sparkles" title="Nothing auto-posted yet">
              Turn on auto-posting under Settings → Automation. Only verified e-invoices from suppliers already in the books are posted.
            </EmptyState>
          ) : (
            <Table rows={a!.autopost} rowKey={(r) => `${r.command_id}-${r.at}`} columns={[
              { key: "c", header: "Client", render: (r) => r.client ?? "—" },
              { key: "s", header: "Result", render: (r) => r.status === "blocked"
                ? <Badge value="held" tone="amber" /> : <Badge value={r.status === "succeeded" ? "posted" : r.status} tone={r.status === "failed" ? "red" : "green"} /> },
              { key: "w", header: "Why held", render: (r) => r.blockers.length
                ? <span className="ui-hint">{r.blockers.map((b) => b.charAt(0).toUpperCase() + b.slice(1)).join(" · ")}</span> : "—" },
              { key: "d", header: "When", render: (r) => <span className="ui-hint">{fmtDate(r.at)}</span> },
            ]} />
          )}
        </Card>
        <Card title="Latest from the books" subtitle="Entries that changed in the last syncs" flush>
          {act.isLoading ? <div style={{ padding: 22 }}><SkeletonRows rows={4} /></div> : (a?.recent.length ?? 0) === 0 ? (
            <EmptyState icon="refresh" title="No entries synced yet">They appear here after the first sync.</EmptyState>
          ) : (
            <div className="ui-conn-list">
              {a!.recent.map((v, i) => (
                <div key={`${v.number}-${i}`} className="ui-conn" style={{ padding: "11px 22px" }}>
                  <ChannelLogo kind={v.provider} size={20} />
                  <div className="ui-conn-main" style={{ minWidth: 0 }}>
                    <span className="ui-cell-title">{v.name ?? "—"}</span>
                    <span className="ui-cell-sub">{humanize(v.voucher_type ?? "entry")}{v.number ? ` ${v.number}` : ""}{ca && v.client ? ` · ${v.client}` : ""}
                      {v.origin === "aawaz" ? " · posted by AawazAI" : ""}</span>
                  </div>
                  <strong className="ui-num">{inr(v.amount)}</strong>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
